import { useCallback } from 'react';
import { useToast } from '@/lib/ui';
import { isAppError } from '@/lib/api/errors';

/**
 * Maps a thrown AppError to the right toast treatment (plan §4.1 / §5.1). Validation errors
 * are handled inline by forms, so this skips them by default. Always surfaces request_id.
 */
type ApiErrorOpts = { onValidation?: (fields: Record<string, string>) => void };

/**
 * The first control on screen the form has just marked invalid.
 *
 * Scoped to the open dialog when there is one: a record page keeps its own
 * fields mounted behind a modal, and the field that failed is the one inside it.
 * Disabled controls are skipped because they cannot take focus.
 */
function firstInvalidControl(): HTMLElement | null {
  const scope =
    document.querySelector('[role="dialog"][data-state="open"]') ??
    document.querySelector('[role="alertdialog"][data-state="open"]') ??
    document;
  return scope.querySelector<HTMLElement>('[aria-invalid="true"]:not([disabled])');
}

/** Run on the next animation frame (or a macrotask where there is none, as in jsdom). */
function afterRender(fn: () => void): void {
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(fn);
  else setTimeout(fn, 0);
}

/**
 * After a refused save renders its inline errors, move focus to the first invalid field (and
 * bring it into view), so a keyboard or screen-reader user lands on the problem instead of
 * <body>. Shared by `useApiError` and by forms that refuse a save locally, before any request.
 * `onNone` runs instead when nothing on screen was marked invalid.
 */
export function focusFirstInvalid(onNone?: () => void): void {
  // React may commit the error state after the next frame, not before it: the state is set
  // from a react-query callback, and the browser is free to run the frame first. So look for
  // a few frames before concluding that nothing on screen was marked (observed in Chromium:
  // the Settings brand fields were marked one frame late and the fallback toast fired).
  let framesLeft = FOCUS_FRAMES;
  const attempt = () => {
    const invalid = firstInvalidControl();
    if (!invalid) {
      framesLeft -= 1;
      if (framesLeft > 0) afterRender(attempt);
      else onNone?.();
      return;
    }
    invalid.focus();
    // A record header is sticky; focus alone can scroll the field under it. Centring is the
    // only position that is safe on every screen. (jsdom has no `scrollIntoView`.)
    if (typeof invalid.scrollIntoView === 'function') invalid.scrollIntoView({ block: 'center' });
  };
  afterRender(attempt);
}

/** How many frames focusFirstInvalid waits for the marked field before giving up. */
const FOCUS_FRAMES = 5;

export function useApiError() {
  const toast = useToast();
  // Rest params keep this assignable to react-query's onError(error, variables, context)
  // for any mutation variable type, while still accepting our optional opts object.
  return useCallback(
    (error: unknown, ...rest: unknown[]) => {
      const opts = rest[0] as ApiErrorOpts | undefined;
      if (!isAppError(error)) {
        toast.error('Something went wrong.', {
          description: error instanceof Error ? error.message : undefined,
        });
        return;
      }
      switch (error.code) {
        case 'validation_error': {
          if (!opts?.onValidation) {
            toast.error('Please fix the highlighted fields.', { requestId: error.requestId });
            break;
          }
          opts.onValidation(error.fieldErrors());
          // The form promised to render the errors inline, but it cannot always keep
          // that promise: a 422 with no details, or one naming a field this screen
          // does not render, would leave a refused save with no feedback anywhere.
          // So the fallback is decided by what actually reached the screen.
          //
          // The same measurement moves focus to the first invalid field, so a
          // keyboard user is not returned to <body> with the error somewhere below.
          // Fixed here, in the shared handler, so every form that reports
          // validation inline inherits it.
          focusFirstInvalid(() =>
            toast.error(error.message || 'That change was refused.', {
              description: 'The server did not accept it, and did not say which field.',
              requestId: error.requestId,
            }),
          );
          break;
        }
        case 'forbidden':
          toast.warning("You don't have permission to do that.");
          break;
        case 'conflict':
          toast.error(error.message || 'That identifier is already in use.', {
            requestId: error.requestId,
          });
          break;
        case 'rate_limited':
          toast.warning('Too many requests. Please wait a moment and try again.');
          break;
        case 'not_found':
          toast.error('That item no longer exists.');
          break;
        default:
          toast.error(error.message || 'Something went wrong.', { requestId: error.requestId });
      }
    },
    [toast],
  );
}
